import {
  PROTOCOL_VERSION,
  type ClientMessage,
  type ServerMessage,
  parseServerMessage,
} from '../../shared/protocol.js';

export type ConnectionStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface ConnectionHandlers {
  onMessage: (message: ServerMessage) => void;
  onStatus: (status: ConnectionStatus, attempt: number) => void;
  onOpen: () => void;
}

const PING_INTERVAL_MS = 15_000;
const WATCHDOG_INTERVAL_MS = 5_000;
const SILENCE_LIMIT_MS = 45_000;
const MAX_BACKOFF_MS = 15_000;

export function wsUrl(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/ws`;
}

export class Connection {
  private socket: WebSocket | null = null;
  private attempt = 0;
  private stopped = true;
  private pingTimer: number | null = null;
  private watchdogTimer: number | null = null;
  private reconnectTimer: number | null = null;
  private lastMessageAt = 0;
  private readonly url = wsUrl();

  constructor(private readonly handlers: ConnectionHandlers) {}

  get isOpen(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    this.attempt = 0;
    this.open();
  }

  stop(): void {
    this.stopped = true;
    this.clearTimers();
    const socket = this.socket;
    this.socket = null;
    if (socket) {
      try {
        socket.close(1000, 'client stopped');
      } catch {
        // ignore
      }
    }
    this.emit('closed');
  }

  send(message: ClientMessage): boolean {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return false;
    try {
      this.socket.send(JSON.stringify(message));
      return true;
    } catch {
      return false;
    }
  }

  ping(): void {
    this.send({ v: PROTOCOL_VERSION, type: 'ping', t: Date.now() });
  }

  private open(): void {
    if (this.stopped) return;
    this.emit(this.attempt === 0 ? 'connecting' : 'reconnecting');

    let socket: WebSocket;
    try {
      socket = new WebSocket(this.url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;

    socket.addEventListener('open', () => {
      if (this.socket !== socket) return;
      this.attempt = 0;
      this.lastMessageAt = Date.now();
      this.startTimers(socket);
      this.emit('open');
      this.handlers.onOpen();
    });

    socket.addEventListener('message', (event) => {
      if (this.socket !== socket) return;
      this.lastMessageAt = Date.now();
      const result = parseServerMessage(String(event.data));
      if (result.ok) this.handlers.onMessage(result.value);
    });

    socket.addEventListener('close', (event) => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.clearTimers();
      if (this.stopped) {
        this.emit('closed');
        return;
      }
      this.scheduleReconnect();
    });

    socket.addEventListener('error', () => {
      try {
        socket.close();
      } catch {
        // ignore
      }
    });
  }

  private scheduleReconnect(): void {
    this.emit('reconnecting');
    const base = Math.min(500 * 2 ** this.attempt, MAX_BACKOFF_MS);
    const delay = Math.round(base * (0.8 + Math.random() * 0.4));
    this.attempt += 1;
    this.reconnectTimer = window.setTimeout(() => this.open(), delay);
  }

  private startTimers(socket: WebSocket): void {
    this.clearTimers();
    this.pingTimer = window.setInterval(() => {
      if (this.isOpen) this.ping();
    }, PING_INTERVAL_MS);
    this.watchdogTimer = window.setInterval(() => {
      if (Date.now() - this.lastMessageAt > SILENCE_LIMIT_MS) {
        try {
          socket.close();
        } catch {
          // ignore
        }
      }
    }, WATCHDOG_INTERVAL_MS);
  }

  private clearTimers(): void {
    if (this.pingTimer !== null) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    if (this.watchdogTimer !== null) {
      window.clearInterval(this.watchdogTimer);
      this.watchdogTimer = null;
    }
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private emit(status: ConnectionStatus): void {
    this.handlers.onStatus(status, this.attempt);
  }
}
